import React, { PropsWithChildren, useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlertGroupName, IAlertState, useAlertState } from '../../hooks/useAlertState/useAlertState';
import { IToastProps, Toast } from '../Toast/Toast';
import { ToastMessageManager } from '../ToastMessageManager/ToastMessageManager';
import { BaseDiv } from '../../common/BaseElements';

export interface IToastProviderProps {
  groupName: AlertGroupName;
}

const ToastContainer = styled(BaseDiv)`
  ${({ theme }) => theme.shadows.dropShadow.e300};
`;

export const getNextAdded = (state: IAlertState[], next: IAlertState[]): IAlertState | undefined => {
  if (next.length > state.length) {
    return next.find((nextItem: IAlertState) => !state.some((stateItem: IAlertState) => stateItem.id === nextItem.id));
  }
};

/* *
 * Only should be used as a child of ToastMessageManager.SnackbarProvider, where `useSnackbar` is available.
 * Usage:
 * <ToastMessageManager.SnackbarProvider anchorOrigin={anchorProps} autoHideDuration={duration}>
 *   <ToastProvider groupName={groupName}><div ref={ref}>{children}</div></ToastProvider>
 * </ToastMessageManager.SnackbarProvider>.
 *
 * Listens to toast group name change.
 * Manages toasts based on alert state change.
 * */
export const ToastProvider: React.FC<PropsWithChildren<IToastProviderProps>> = ({ children, groupName }) => {
  const { enqueueSnackbar, closeSnackbar } = ToastMessageManager.useSnackbar();
  const { state, removeAlertState } = useAlertState(groupName);
  const [previousState, setPreviousState] = useState(state);

  useEffect(() => {
    const nextAdded = getNextAdded([...previousState], [...state]);
    if (nextAdded) {
      if (!nextAdded.id) {
        // pupulate a random id if not passed one.
        nextAdded.id = Date.now().toString();
      }

      enqueueSnackbar(`default`, {
        key: nextAdded.id,
        persist: nextAdded.persist || nextAdded.isProcessing,
        onExited: (_, key) => removeAlertState(groupName, key as string),
        content: (key) => {
          const toastProps: IToastProps = {
            children: nextAdded.children,
            isProcessing: nextAdded.isProcessing,
            processingLabel: nextAdded.processingLabel,
            undoLabel: nextAdded.undoLabel,
            onUndo: nextAdded.onUndo
          };
          return (
            <ToastContainer>
              <Toast
                id={key as string}
                onClose={() => {
                  closeSnackbar(key);
                  if (nextAdded.onClose) {
                    nextAdded.onClose();
                  }
                }}
                {...toastProps}
              />
            </ToastContainer>
          );
        }
      });
    }
    setPreviousState(state);
  }, [state]);

  return <>{children}</>;
};
